import { Box, Typography } from "@mui/material";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { FONT_SIZE, HOLIDAY_GROUPS, INK, STATUS } from "../../../theme/tokens";
import { JumpingDots } from "../../UIToolkit/Animations/JumpingDots";
import SystemDialog from "../../UIToolkit/Popups/Dialog";
import { HolidayDayPartSelect } from "./HolidayDayPartSelect";
import { HolidaySmsField, NO_SMS, smsMissingText, smsToSend } from "./HolidaySmsField";
import { HolidayWorkingList } from "./HolidayWorkingList";
import {
	type HolidayDayPart,
	type HolidayGroup,
	type HolidayGroupChangePerson,
	HolidayGroupSkipReason,
	type HolidayGroupUpdateResult,
	HolidayStatus,
} from "./holidayApi";
import { holidayGroupById, summarizeNames } from "./holidayFormat";
import { useHolidayPeopleByIds, useMoveToHolidayGroup, useUpcomingHolidays } from "./holidayQueries";

type Props = {
	people: readonly HolidayGroupChangePerson[];
	groups: readonly HolidayGroup[];
	/** Null = Not assigned. */
	targetGroupId: string | null;
	onClose: () => void;
	/** After a save — e.g. to clear a bulk selection. */
	onMoved?: (result: HolidayGroupUpdateResult) => void;
};

/**
 * "Move Maria Alvarez to group B?" — the one popup every place that changes a person's holiday group opens:
 * a Team row, a bulk selection, the Not assigned list and the profile.
 */
export const HolidayGroupChangeDialog: FC<Props> = ({ people, groups, targetGroupId, onClose, onMoved }) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const onePerson = people.length === 1 ? people[0] : null;
	// Null = Not assigned; undefined = the group was removed (in another tab) since the popup was opened.
	const foundGroup = targetGroupId === null ? null : holidayGroupById(groups, targetGroupId);
	const groupRemoved = foundGroup === undefined;
	const upcoming = useUpcomingHolidays(departmentId);
	// Only one person moving to a group gets a day part choice, so only they are looked up.
	const onePersonId = onePerson?.nurseId;
	const movesToGroup = targetGroupId !== null;
	const personIds = useMemo(() => (onePersonId !== undefined && movesToGroup ? [onePersonId] : []), [onePersonId, movesToGroup]);
	const personQuery = useHolidayPeopleByIds(departmentId, personIds);
	const person = onePerson === null ? undefined : personQuery.data?.get(onePerson.nurseId);
	// A move calculates the day part again from their shifts, so that's what it shows. Sent only when changed.
	const currentDayPart = person === undefined ? null : person.calculated.dayPart;
	const [pickedDayPart, setPickedDayPart] = useState<HolidayDayPart | null>(null);
	const [sms, setSms] = useState(NO_SMS);
	const move = useMoveToHolidayGroup();
	// Holidays already on the schedule change right away for the people moved. Unknown until they load.
	const appliedNames = upcoming.data?.filter((holiday) => holiday.status !== HolidayStatus.NotApplied).map((holiday) => holiday.name);
	// Saving before we know which holidays it changes, or the person's day part, would move them blind.
	const notReady = upcoming.data === undefined || (personIds.length > 0 && personQuery.data === undefined);

	// Moving people to a group that no longer exists can't work, so the popup closes and says why.
	const closedForRemovedGroup = useRef(false);
	useEffect(() => {
		if (!groupRemoved || closedForRemovedGroup.current) return;
		closedForRemovedGroup.current = true;
		showSnackbar(t("holidays.groupChange.groupRemoved"), "warning");
		onClose();
	}, [groupRemoved, showSnackbar, t, onClose]);
	if (foundGroup === undefined) return null;
	const targetGroup = foundGroup;

	const name = onePerson?.name;
	const titleKey =
		targetGroup === null
			? onePerson
				? "holidays.groupChange.titleOne.none"
				: "holidays.groupChange.titleMany.none"
			: onePerson
				? "holidays.groupChange.titleOne.group"
				: "holidays.groupChange.titleMany.group";
	const title = t(titleKey, { name, count: people.length, group: targetGroup?.name });
	const confirm = targetGroup === null ? t("holidays.groupChange.confirm.none") : t("holidays.groupChange.confirm.group", { group: targetGroup.name });

	const handleMoved = (result: HolidayGroupUpdateResult) => {
		const notInDepartment = result.skipped.filter((skip) => skip.reason === HolidayGroupSkipReason.NotInDepartment).length;
		if (result.updated.length > 0) {
			// One person reads by name; several by how many actually moved.
			const doneKey = onePerson
				? targetGroup === null
					? "holidays.groupChange.doneOne.none"
					: "holidays.groupChange.doneOne.group"
				: targetGroup === null
					? "holidays.groupChange.done.none"
					: "holidays.groupChange.done.group";
			showSnackbar(t(doneKey, { name, count: result.updated.length, group: targetGroup?.name }), "success");
		} else if (notInDepartment === 0) {
			showSnackbar(t("holidays.groupChange.unchanged"), "info");
		}
		if (notInDepartment > 0) showSnackbar(t("holidays.groupChange.notInDepartment", { count: notInDepartment }), "warning");
		onMoved?.(result);
		onClose();
	};

	// The mutation reports a failure itself; the popup stays open so the scheduler can try again.
	const handleConfirm = () => {
		if (smsMissingText(sms)) {
			showSnackbar(t("holidays.sms.missing"), "warning");
			return;
		}
		move.mutate(
			{
				nurseIds: people.map((p) => p.nurseId),
				groupId: targetGroupId,
				dayPart: targetGroup !== null && onePerson !== null && pickedDayPart !== null && pickedDayPart !== currentDayPart ? pickedDayPart : null,
				smsMessage: smsToSend(sms),
			},
			{ onSuccess: handleMoved },
		);
	};
	// Someone with no shifts in 6 weeks gets no day part, so no holiday shift, until one is picked.
	const noDayPart = targetGroup !== null && onePerson !== null && personQuery.data !== undefined && (pickedDayPart ?? currentDayPart) === null;

	const body = (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: { sm: HOLIDAY_GROUPS.DIALOG_BODY_MIN_WIDTH_PX } }}>
			{onePerson ? (
				<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, color: INK.SECONDARY, fontSize: FONT_SIZE.EMPHASIS }}>
					{onePerson.jobLabel && <Box component="span">{onePerson.jobLabel}</Box>}
					{onePerson.jobLabel && targetGroup !== null && (
						<Box component="span" aria-hidden sx={{ color: INK.MUTED }}>
							·
						</Box>
					)}
					{targetGroup !== null && (
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Box component="span">{t("holidays.groupChange.dayPartLabel")}</Box>
							{/* Until the person loads their day part isn't known, and an empty select would read "No day part". */}
							{personQuery.isError ? (
								<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: "error.main" }} data-testid="holiday-group-change-day-part-error">
									{t("holidays.groupChange.dayPartLoadError", { name })}
								</Typography>
							) : personQuery.data === undefined ? (
								<Box data-testid="holiday-group-change-day-part-loading" sx={{ minWidth: HOLIDAY_GROUPS.SELECT_MIN_WIDTH_PX }}>
									<JumpingDots />
								</Box>
							) : (
								<HolidayDayPartSelect
									value={pickedDayPart ?? currentDayPart}
									onChange={setPickedDayPart}
									ariaLabel={t("holidays.groupChange.dayPartLabel")}
									disabled={move.isPending}
									sx={{ minWidth: HOLIDAY_GROUPS.SELECT_MIN_WIDTH_PX }}
									dataTestId="holiday-group-change-day-part"
								/>
							)}
						</Box>
					)}
				</Box>
			) : (
				<Typography sx={{ color: INK.SECONDARY, fontSize: FONT_SIZE.EMPHASIS }} data-testid="holiday-group-change-names">
					{summarizeNames(
						t,
						people.map((p) => p.name).sort((a, b) => a.localeCompare(b)),
					)}
				</Typography>
			)}
			{noDayPart && (
				<Typography sx={{ color: STATUS.WARNING.dark, fontSize: FONT_SIZE.EMPHASIS }} data-testid="holiday-group-change-no-day-part">
					{t("holidays.groupChange.noDayPart", { name })}
				</Typography>
			)}
			{targetGroup !== null ? (
				<HolidayWorkingList
					variant="list"
					holidays={upcoming.data}
					isError={upcoming.isError}
					groupId={targetGroup.id}
					dataTestId="holiday-group-change-holidays"
				/>
			) : (
				<>
					<Typography sx={{ color: INK.SECONDARY, fontSize: FONT_SIZE.EMPHASIS }}>{t("holidays.groupChange.keepUsual")}</Typography>
					{upcoming.isError && (
						<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: "error.main" }} data-testid="holiday-group-change-load-error">
							{t("holidays.groupChange.loadError")}
						</Typography>
					)}
				</>
			)}
			{appliedNames !== undefined && appliedNames.length > 0 && (
				<>
					<Typography sx={{ color: INK.SECONDARY, fontSize: FONT_SIZE.EMPHASIS }} data-testid="holiday-group-change-applied-note">
						{t("holidays.groupChange.appliedNote", { names: summarizeNames(t, appliedNames), count: appliedNames.length })}
					</Typography>
					<HolidaySmsField
						sms={sms}
						onChange={setSms}
						label={onePerson ? t("holidays.sms.labelOne", { name }) : t("holidays.sms.labelMany")}
						disabled={move.isPending}
						dataTestId="holiday-group-change-sms"
					/>
				</>
			)}
		</Box>
	);

	return (
		<SystemDialog
			open
			onClose={onClose}
			title={title}
			body={body}
			cancel={t("cancel")}
			confirm={confirm}
			confirmButtonVariant="primary"
			onConfirm={handleConfirm}
			confirmLoading={move.isPending}
			confirmDisabled={notReady}
			maxWidth="sm"
			dataTestId="holiday-group-change-dialog"
			confirmDataTestId="holiday-group-change-confirm"
			cancelDataTestId="holiday-group-change-cancel"
		/>
	);
};
